import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PANELS } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", panels: [], active: true, see_financials: false };
const err = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Operazione non riuscita");

function UserDialog({ value, onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState(value.id ? { ...value, password: "" } : EMPTY);
  const toggle = (p) => setF({ ...f, panels: f.panels.includes(p) ? f.panels.filter((x) => x !== p) : [...f.panels, p] });
  const save = async () => {
    try {
      const body = { name: f.name, email: f.email, password: f.password || null, panels: f.panels, active: f.active, see_financials: !!f.see_financials };
      if (value.id) await api.patch(`/users/${value.id}`, body); else await api.post("/users", body);
      toast.success(value.id ? "Utente aggiornato" : "Utente creato");
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    } catch (e) { toast.error(err(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{value.id ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></div>
          <div className="space-y-1.5">
            <Label>{value.id ? "Nuova password (lascia vuoto per non cambiarla)" : "Password iniziale (min. 8 caratteri)"}</Label>
            <Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" />
          </div>
          <div>
            <Label>Pannelli accessibili</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {PANELS.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={f.panels.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`user-panel-${p.id}`} />{p.label}
                </label>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Utenti e collegamento Shopify restano riservati al proprietario.</p>
          </div>
          <label className="flex items-start gap-2 rounded-md border border-border/60 p-3 text-sm">
            <Checkbox className="mt-0.5" checked={!!f.see_financials} onCheckedChange={(v) => setF({ ...f, see_financials: !!v })} data-testid="user-see-financials" />
            <span>Può vedere i dati finanziari<span className="block text-[11px] text-muted-foreground">Importi in euro nei pannelli a cui ha accesso, pannello Vendite ed export. Se non spunti, gli importi restano nascosti.</span></span>
          </label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-active" />Utente attivo</label>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.name || !f.email} data-testid="user-save">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Utenti() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(null);
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utente eliminato"); qc.invalidateQueries({ queryKey: ["users"] }); }
    catch (e) { toast.error(err(e)); }
  };
  const label = (id) => PANELS.find((p) => p.id === id)?.label || id;
  return (
    <div data-testid="utenti-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Utenti</h1>
          <p className="text-sm text-muted-foreground">Chi può accedere al gestionale e a quali pannelli</p>
        </div>
        <Button onClick={() => setEdit({})} data-testid="new-user-btn"><Plus className="mr-2 h-4 w-4" />Nuovo utente</Button>
      </div>
      <div className="space-y-3">
        {users.map((u) => (
          <Card key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`user-row-${u.email}`}>
            <div className="min-w-0">
              <p className="font-medium">{u.name} {u.active === false && <Badge variant="outline" className="ml-1 text-[10px] text-rose-400">Disattivato</Badge>}</p>
              <p className="text-xs text-muted-foreground">{u.email}</p>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {u.role === "admin" ? <Badge className="text-[10px]">Proprietario · accesso completo</Badge>
                  : (u.panels || []).length ? u.panels.map((p) => <Badge key={p} variant="outline" className="text-[10px]">{label(p)}</Badge>)
                  : <span className="text-[11px] text-muted-foreground">Nessun pannello</span>}
                {u.role !== "admin" && u.see_financials && <Badge className="bg-emerald-500/15 text-[10px] text-emerald-400" data-testid={`user-financial-badge-${u.email}`}>Dati finanziari</Badge>}
              </div>
            </div>
            {u.role !== "admin" && (
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => setEdit(u)} data-testid={`user-edit-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`user-delete-${u.email}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
              </div>
            )}
          </Card>
        ))}
      </div>
      {edit && <UserDialog value={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}
